import { FC, useState, useEffect, useMemo, useContext, useRef, JSX } from "react";
import { Link, RouteComponentProps, useHistory } from "react-router-dom";
import moment, { Moment } from "moment";
import { camelCase } from "lodash";
import { toast } from "react-toastify";
import { AccordionContainer, Accordion, Breadcrumbs, Page, PageHeader, PageHeaderLeft, PageHeaderRight, PageInner, PageSection, LoadingSpinner, FilterCriteria, FilterCriteriaModel, FilterCriteriaOnChangeResponse, breadcrumbTemplates, BreadcrumbTemplateEnum } from "@app/shared";
import { QESButton, QESCheckbox, QESSelect, QESAlert, QESMultiSelect } from "@app/shared/qes-standards";
import { SortDirections } from "@app/shared/waffles";
import { MultiselectItem, MultiselectOnChangeResponse } from "@app/shared/qes-standards/forms/Multiselect";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { ProviderNetworkType } from "@app/projects/enumerators";
import { ResourceType } from "@app/resources";
import CreateFilteredResourceModel from "@app/resources/createFilteredResourceModel";
import { RoleRestricted, Roles, UserContext } from "@app/user";
import { ProviderSetType, ProviderSetVersionSummaryType, ProviderSetDataSortColumn, ProviderSetsRequestType, ProviderSetVersionStatusEnum, ProviderSetVersionValidationStatusEnum } from "@app/provider-sets/enumerations";
import ProviderSetService from "@app/provider-sets/providerSetService";
import { ProviderSet, ProviderSetVersionModel, ProviderSetHistoryModel, UpdateProviderSetModel, ProviderSetUploadModel } from "@app/provider-sets/providerSet";
import { PageSettings, ProviderDetailsGrid, ProviderDetailsGridOnUpdateResponse } from "@app/provider-sets/ProviderDetailsGrid";
import CreatePanelContent from "@app/provider-sets/CreatePanelContent";
import ClonePanelContent from "@app/provider-sets/ClonePanelContent";
import { OverallSummary } from "./OverallSummary";
import { SummaryInfo } from "./SummaryInfo";
import { ValidationInfo } from "./ValidationInfo";
import ArchivePanelContent from "./ArchivePanelContent";
import EditPanelContent from "./EditPanelContent";
import PurgePanelContent from "./PurgePanelContent";
import { FlyoutLink } from "@app/shared/Flyouts";
import { Modal } from "@app/shared/Modals";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QesIcon } from "@app/shared/icons/QESIcon";
import ProviderValidationService from "../providerValidationService";
import { FeatureFlagContext, Features } from "../../feature-flags";
import { Message, MessageType } from "../../notifications";
import { NotificationContext } from "../../notifications/NotificationContext";
import { ProviderValidationStatusChangeNotificationMessagePayload } from "../../notifications/message";

interface NewVersionModalContent {
  providerSetId: number;
  criteria: FilterCriteriaModel[];
  confirmPrompt: JSX.Element;
  afterSave: (newVersion: ProviderSetVersionModel) => void;
  afterError: () => void;
  filterInvalidRecords?: boolean;
}

interface ProviderSetDetailMatchProps {
  providerSetId?: string;
  versionId?: string;
}

type ProviderSetDetailProps = RouteComponentProps<ProviderSetDetailMatchProps, undefined, undefined>;

type TabViews = "Preview" | "Summary Information" | "Validation Information";

interface HistoryEntry {
  date: Moment,
  display: JSX.Element
}

export const ProviderSetDetail: FC<React.PropsWithChildren<ProviderSetDetailProps>> = (props: ProviderSetDetailProps) => {
  const providerSetService = new ProviderSetService();
  const providerValidationService = new ProviderValidationService();

  const user = useContext(UserContext);
  const notificationContext = useContext(NotificationContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;

  const history = useHistory();

  const [providerSet, setProviderSet] = useState<ProviderSet>();
  const [sourceProviderSet, setSourceProviderSet] = useState<ProviderSet>();
  const [availableFilteredProviderSets, setAvailableFilteredProviderSets] = useState<ProviderSet[]>();
  const [selectedFilteredProviderActivateOptions, setSelectedFilteredProviderActivateOptions] = useState<ProviderSet[]>([])
  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [selectedVersion, setSelectedVersion] = useState<ProviderSetVersionModel>(null);
  const [selectedVersionSource, setSelectedVersionSource] = useState<ProviderSetVersionModel>(null);
  const [selectedVersionOrigFilterCriteria, setSelectedVersionOrigFilterCriteria] = useState<FilterCriteriaModel[]>([]);
  const [selectedVersionFilterCriteria, setSelectedVersionFilterCriteria] = useState<FilterCriteriaModel[]>([]);
  const [maxActivatedVersion, setMaxActivatedVersion] = useState<ProviderSetVersionModel>(null);
  const [filterCriteriaHasChanges, setFilterCriteriaHasChanges] = useState(false);
  const [selectedCompareVersionId, setSelectedCompareVersionId] = useState("");
  const [selectedCompareVersion, setSelectedCompareVersion] = useState<ProviderSetVersionModel>(null);
  const [showSharedBtn, setShowSharedBtn] = useState(false);
  const [showEditBtn, setShowEditBtn] = useState(false);
  const [showCreateFilterBtn, setShowCreateFilterBtn] = useState(false);
  const [showCloneFilterBtn, setShowCloneFilterBtn] = useState(false);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [pageloading, setPageLoading] = useState(false);
  const [sidePanelLoading, setSidePanelLoading] = useState(false);
  const [newVersionModalContent, setNewVersionModalContent] = useState<NewVersionModalContent>(null);
  const [newProviderSetUploadId, setNewProviderSetUploadId] = useState<number>(null);
  const versionDropdownRef = useRef<HTMLSelectElement>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);

  const [activeTab, setActiveTab] = useState<TabViews>("Summary Information");

  const [alerts, setAlerts] = useState<JSX.Element[]>([]);

  const [activeSummaryInfoAccordion, setActiveSummaryInfoAccordion] = useState<ProviderSetVersionSummaryType>(ProviderSetVersionSummaryType.Unknown);
  const [activeValidationInfoAccordion, setActiveValidationInfoAccordion] = useState<ProviderSetVersionSummaryType>(ProviderSetVersionSummaryType.Unknown);
  const [totalRecords, setTotalRecords] = useState(0);
  const [previewPageSettings, setPreviewPageSettings] = useState<PageSettings>(
    {
      skip: 0,
      sortDetails: {
        sortDirection: SortDirections.Asc,
        sortedColumn: ProviderSetDataSortColumn.NPI,
      },
    });
  const [providerSetHistory, setProviderSetHistory] = useState<ProviderSetHistoryModel>(null);

  const [showHistory, setShowHistory] = useState(false);
  const [filterColumnNames, setFilterColumnNames] = useState<string[]>([]);
  const [isOwner, setIsOwner] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isVersionUploadFinished, setIsVersionUploadFinished] = useState(false);
  const [isIndexCreationPending, setIsIndexCreationPending] = useState(false);
  const [showCreateIndexModal, setShowCreateIndexModal] = useState(false);
  const [indexModalWarningOverride, setIndexModalWarningOverride] = useState(false);
  const [showNPIModal, setShowNPIModal] = useState(false);
  const [isJobRunning, setIsJobRunning] = useState(false);
  const [filterInvalidRecords, setFilterInvalidRecords] = useState(false);


  const breadcrumbItemsBase = breadcrumbTemplates[BreadcrumbTemplateEnum.ProviderSets];
  const breadcrumbCurrentPage = {
    text: (!!providerSet ? providerSet.resource.name : ""),
  };
  const breadcrumbReturnLink = {
    iconClass: "fa-user-md",
    link: `/provider-sets/${props.match.params.providerSetId}/detail`,
    text: (!!providerSet ? providerSet.resource.name : ""),
  };

  useEffect(() => {
    //subscribe and unsubscribe from notifications
    notificationContext.subscribe(handleNotifications);

    return () => {
      notificationContext.unsubscribe(handleNotifications);
    };
  }, [selectedVersion, providerSet]);

  useEffect(() => {
    if (!!props.match.params.providerSetId) {
      setActiveTab("Summary Information");
      setSelectedVersion(null);
      setSelectedVersionId(null);
      fetchProviderSetDetail(props.match.params.providerSetId);
    }
  }, [props.match.params.providerSetId]);

  useEffect(() => {
    if (!!providerSet && providerSet.providerSetVersions.length > 0) {
      if (!!props.match.params.versionId) {
        setSelectedVersionId(props.match.params.versionId);
      } else {
        const sortedVersions = providerSet.providerSetVersions.sort((a, b) => b.providerSetVersionId - a.providerSetVersionId);
        setSelectedVersionId(sortedVersions[0].providerSetVersionId.toString());
      }

      const sortedActiveVersions = providerSet.providerSetVersions.sort((a, b) => {
        const aDate = (!!a.dateActivated ? moment(a.dateActivated) : moment.utc("0001-01-01"));
        const bDate = (!!b.dateActivated ? moment(b.dateActivated) : moment.utc("0001-01-01"));

        return bDate.valueOf() - aDate.valueOf();
      });

      if (sortedActiveVersions.length > 0 && !!sortedActiveVersions[0].dateActivated) {
        setMaxActivatedVersion(sortedActiveVersions[0]);
      } else {
        setMaxActivatedVersion(null);
      }

    } else {
      setSelectedVersionId("");
    }
    setSelectedCompareVersionId("");
  }, [providerSet, props.match.params.versionId])

  const updateVersionInfo = async (versionId: string) => {
    let version: ProviderSetVersionModel = null;
    let sourceVersion: ProviderSetVersionModel = null;
    let criteria: FilterCriteriaModel[] = [];

    if (!!versionId && !!providerSet)
      version = providerSet.providerSetVersions.find((x) => { return x.providerSetVersionId.toString() === versionId });

    if (!!sourceProviderSet && !!version) {
      sourceVersion = sourceProviderSet.providerSetVersions.find((x) => { return x.providerSetVersionId === version.sourceProviderSetVersionId });
      if (!!version.filterCriteriaJson) {
        criteria = JSON.parse(version.filterCriteriaJson, function (prop, value) {
          const camelProp = camelCase(prop);
          if (prop === camelProp)
            return value
          else
            this[camelProp] = value;
        });
      }
    }

    setSelectedVersion(version);
    setSelectedVersionSource(sourceVersion);
    setSelectedVersionOrigFilterCriteria(criteria);
    setSelectedVersionFilterCriteria(criteria);
    setFilterInvalidRecords(version.filterInvalidRecords);

    if (versionId === selectedCompareVersionId)
      setSelectedCompareVersionId("");

    if (!!versionId)
      history.replace(`/provider-sets/${props.match.params.providerSetId}/version/${versionId}/detail`);
  }

  useEffect(() => {
    if (!selectedVersionId)
      return;
    let statusInterval: NodeJS.Timeout;
    const checkStatus = async () => {
      const status = await loadStatus();
      if (status) {
        clearInterval(statusInterval);
      }
    }

    if (!!selectedVersionId) {
      checkStatus();
      statusInterval = setInterval(checkStatus, 5000);
      updateVersionInfo(selectedVersionId);
    }
    return () => clearInterval(statusInterval);
  }, [selectedVersionId, sourceProviderSet]);

  useEffect(() => {
    let version: ProviderSetVersionModel = null;

    if (!!selectedCompareVersionId && !!providerSet && !!providerSet.providerSetVersions)
      version = providerSet.providerSetVersions.find((x) => { return selectedCompareVersionId === x.providerSetVersionId.toString() });

    setSelectedCompareVersion(version);

    if (!!version && activeTab === "Preview")
      setActiveTab("Summary Information");

  }, [selectedCompareVersionId]);

  const loadStatus = async (): Promise<boolean> => {
    const statusDetails = await providerSetService.getProviderSetVersionStatus(props.match.params.providerSetId, selectedVersionId);
    setIsIndexCreationPending(statusDetails.pendingIndexCreation);

    if (statusDetails.hasIndexes === false) {
      setIsVersionUploadFinished(statusDetails.isUploadFinished);
      if (statusDetails.pendingIndexCreation === true) {
        setAlerts([getIndexPendingAlert()]);
      } else {
        setShowCreateIndexModal(true);
      }
    }

    setStatusLoaded(true);
    return statusDetails.hasIndexes === true && statusDetails.isUploadFinished === true && statusDetails.pendingIndexCreation === false;
  }

  const filteredProviderActivateOptions = useMemo(() => {
    const options: { [key: string]: MultiselectItem } = {};

    if (!!providerSet && !!availableFilteredProviderSets) {
      availableFilteredProviderSets.forEach((provider) => Object.assign(options,
        {
          [provider.resource.resourceId]: {
            currentState: selectedFilteredProviderActivateOptions.some((x) => x.resource.resourceId === provider.resource.resourceId) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
            displayValue: provider.resource.name,
          }
        }));
    }

    return options;
  }, [providerSet, selectedFilteredProviderActivateOptions]);

  const updateFilteredProviderActivateOptions = (response: MultiselectOnChangeResponse) => {
    const selectedProviders = response.checkedKeys;
    const newProviders = selectedProviders.map((providerId) => {
      const matchedRoleModel = providerSet.filteredProviderSets.find((x) => x.resource.resourceId.toString() === providerId);
      return matchedRoleModel;
    });
    setSelectedFilteredProviderActivateOptions(newProviders);
  }

  const getIndexPendingAlert = () => {
    return (
      <QESAlert key="IndexPendingAlert" type="info" persist={true} >
        <div>
          <div className="mt-1 pull-left"><strong>Please wait!</strong> The network version index creation is in progress.</div>
        </div>
      </QESAlert>);
  }

  const getActivationAlerts = (provSet: ProviderSet, version: ProviderSetVersionModel, versionCriteria: FilterCriteriaModel[]): JSX.Element => {
    let alert: JSX.Element = null;

    if (!version.dateActivated && (!maxActivatedVersion || version.dateCreated > maxActivatedVersion.dateActivated)) {
      if (!!provSet.sourceProviderSetId) {
        const filterInvalidRecordsContext = selectedVersionSource.providerSetVersionValidationStatus == ProviderSetVersionValidationStatusEnum.Invalid && user.roles.includes(Roles.PROVIDER_VALIDATION_USER) && filterInvalidRecords
        if ((versionCriteria.length === 0 || selectedVersionOrigFilterCriteria.length === 0) && !filterInvalidRecordsContext) {
          alert = (<QESAlert key="ActivateAlert" type="warning" persist={true}><strong>Warning!</strong> This network version has not been activated. It needs filter criteria!</QESAlert>);
        } else {
          alert = (
            <QESAlert key="ActivateAlert" type="warning" persist={true}>
              <div>
                <div className="mt-1 pull-left"><strong>Warning!</strong> This network version has not been activated.</div>
                <QESButton
                  qesTestId="provider-detail__activate"
                  qesType="secondary"
                  className="pull-right mt-1"
                  onClick={() => handleActivate()}
                >
                  Activate
                </QESButton>
              </div>
            </QESAlert>);
        }
      } else {
        const filteredProvidersMultiSelect = (
          <>
            <QESMultiSelect
              containerClassName="col-8"
              label="These associated filtered networks can also be activated"
              onChange={updateFilteredProviderActivateOptions}
              options={filteredProviderActivateOptions}
              testId="provider-detail__activated-filtered-providers"
            />
            <div className="mb-3">
              <QESButton
                qesTestId="provider-detail__select-all-filtered-providers"
                qesType="secondary"
                onClick={() => setSelectedFilteredProviderActivateOptions(availableFilteredProviderSets)}>
                Select All
              </QESButton>
              <QESButton
                qesTestId="provider-detail__clear-all-filtered-providers"
                qesType="secondary"
                onClick={() => setSelectedFilteredProviderActivateOptions([])}>
                Clear All
              </QESButton>
            </div>
          </>
        );

        alert = (
          <div key="ActivateAlert">
            <QESAlert type="warning" persist={true}>
              <div>
                <div className="mt-1 pull-left"><strong>Warning!</strong> This network version has not been activated.</div>
                <QESButton
                  qesTestId="provider-detail__activate"
                  qesType="secondary"
                  className="pull-right mt-1"
                  onClick={() => handleActivate()}
                  disabled={!newProviderSetUploadId}
                >
                  Activate
                </QESButton>
              </div>
            </QESAlert>
            {!!availableFilteredProviderSets && availableFilteredProviderSets.length > 0 ?
              <>{filteredProvidersMultiSelect}</> : null
            }
          </div>);
      }
    }

    return alert;
  }

  const getValidationAlert = (version: ProviderSetVersionModel): JSX.Element => {
    let alert: JSX.Element = null;

    if (version.providerSetVersionStatus == ProviderSetVersionStatusEnum.Activated && version.sourceProviderSetVersionId == null) {
      if (version.providerSetVersionValidationStatus == ProviderSetVersionValidationStatusEnum.Unknown) {
        alert = (
          <QESAlert key="ValidateAlert" type="info" persist={true}>
            <div>
              <div className="mt-1 pull-left">This network version has not been validated.</div>
              <QESButton
                qesTestId="provider-detail__validate"
                qesType="secondary"
                className="pull-right mt-1"
                onClick={() => queueProviderValidationJob()}
              >
                Validate
              </QESButton>
            </div>
          </QESAlert>);
      }
      else if (version.providerSetVersionValidationStatus == ProviderSetVersionValidationStatusEnum.Processing) {
        alert = (
          <QESAlert key="ValidateProcessingAlert" type="info" persist={true}>
            <strong>Please Wait!</strong> The network version validation is in progress.
          </QESAlert>);
      }
      else if (version.providerSetVersionValidationStatus == ProviderSetVersionValidationStatusEnum.Queued) {
        alert = (
          <QESAlert key="ValidateQueuedAlert" type="info" persist={true}>
            <strong>Please Wait!</strong> The network version validation has been queued.
          </QESAlert>);
      }
      else if (version.providerSetVersionValidationStatus == ProviderSetVersionValidationStatusEnum.Failed) {
        alert = (
          <QESAlert key="ValidateFailedAlert" type="danger" persist={true}>
            <div>
              <div className="mt-1 pull-left">The network version could not be validated. Please try validating again</div>
              <QESButton
                qesTestId="provider-detail__validate"
                qesType="secondary"
                className="pull-right mt-1"
                onClick={() => queueProviderValidationJob()}
              >
                Validate
              </QESButton>
            </div>
          </QESAlert>);
      }
      else if (version.providerSetVersionValidationStatus == ProviderSetVersionValidationStatusEnum.Invalid) {
        alert = (
          <>
            <QESAlert key="ValidateAlert" type="info" persist={true}>
              <div>
                <div className="mt-1 pull-left">{`This network version was last validated on ${(!!version ? moment.utc(version.providerValidationJobRunDate).local().format("MMM DD, YYYY h:mm:ss A") : "")}. Re-validating will apply any recent updates to CCIIO validation rules or reference data.`}</div>
                <QESButton
                  qesTestId="provider-detail__revalidate"
                  qesType="secondary"
                  className="pull-right mt-1"
                  onClick={() => queueProviderValidationJob()}
                >
                  Re-Run Validation
                </QESButton>
              </div>
            </QESAlert>
            <QESAlert key="ValidatedInvalidAlert" type="danger" persist={true}>
              <div className="mt-1 pull-left">Validation Status: Completed – Errors Detected</div>
            </QESAlert>
          </>)
      }
    }

    return alert;
  }

  const handleNotifications = async (message: Message) => {
    if (message && message.notificationType === MessageType.ProviderValidationStatusChange) {
      const payload = message.payload as ProviderValidationStatusChangeNotificationMessagePayload;
      if (payload.providerSetId == selectedVersion.providerSetId && payload.providerSetVersionId == selectedVersion.providerSetVersionId) {
        fetchProviderSetDetail(providerSet.resource.resourceId.toString());
      }
    }
  };

  const getOutOfDateFilteredProviders = (provSet: ProviderSet): JSX.Element[] => {
    let oodFilteredProviderSets: { [key: string]: number };

    if (!!provSet && !!provSet.outOfDateFilteredProviderSets) {
      oodFilteredProviderSets = provSet.outOfDateFilteredProviderSets;
    }

    return oodFilteredProviderSets ? Object.entries(oodFilteredProviderSets).map(([key, value]) => (<li key={key}>{`${key}: ${value}`}</li>)) : [];
  }

  const getOutOfDateFilteredProviderSetsAlert = (provSet: ProviderSet): JSX.Element => {
    let alert: JSX.Element = null;
    const outOfDateFilteredProviders = getOutOfDateFilteredProviders(provSet);

    if (outOfDateFilteredProviders.length > 0) {
      alert = (
        <QESAlert
          key="OODFilterAlert"
          type="warning"
          collapseTitle={<><strong>Warning!</strong> The listed filtered networks are using a previous version of this network</>}
          persist={true}>
          {outOfDateFilteredProviders}
        </QESAlert>
      );
    }

    return alert;
  }

  useEffect(() => {
    if (!!providerSet && !!selectedVersion) {
      const owner = (providerSet.resource.userId === user.userId || (user.roles.some(x => x === Roles.RESOURCE_ADMIN) && !providerSet.resource.isPrivate));
      setIsOwner(owner);

      setShowSharedBtn(owner && !providerSet.resource.isArchived && providerSet.networkType !== ProviderNetworkType.Market &&
        providerSet.providerSetVersions.length > 0 && !providerSet.providerSetVersions.some(x => !!x.dateActivated));
      setShowEditBtn(owner && providerSet.networkType !== ProviderNetworkType.Market);
      setShowCreateFilterBtn(!providerSet.resource.isArchived && providerSet.resource.resourceType !== ResourceType.FilteredProviderSet && !!maxActivatedVersion && selectedVersion.providerSetVersionValidationStatus != "processing"
        && selectedVersion.providerSetVersionValidationStatus != "queued"  && ((owner && providerSet.providerSetType !== ProviderSetType.NYSDOH) || (providerSet.resource.userId !== user.userId && providerSet.networkType !== ProviderNetworkType.Market)));
      setShowCloneFilterBtn(!providerSet.resource.isArchived && providerSet.resource.resourceType === ResourceType.FilteredProviderSet);

      setShowHistory((owner || providerSet.networkType === ProviderNetworkType.Market) && providerSet.resource.resourceType !== ResourceType.FilteredProviderSet);

      const tempAlerts: JSX.Element[] = [];
      if (statusLoaded === true && isIndexCreationPending === false && showCreateIndexModal === false) {
        tempAlerts.push(getActivationAlerts(providerSet, selectedVersion, selectedVersionFilterCriteria));
      }

      tempAlerts.push(getOutOfDateFilteredProviderSetsAlert(providerSet));

      if (user.roles.includes(Roles.PROVIDER_VALIDATION_USER) && featureFlags.has(Features.CCIIO_PROVIDER_VALIDATIONS))
        tempAlerts.push(getValidationAlert(selectedVersion));

      setAlerts(tempAlerts);

    } else {
      setShowSharedBtn(false);
      setShowEditBtn(false);
      setShowCreateFilterBtn(false);
      setShowCloneFilterBtn(false);
      setShowHistory(false);
      setIsOwner(false);
    }
  }, [providerSet, selectedVersion, maxActivatedVersion, selectedFilteredProviderActivateOptions, selectedVersionFilterCriteria, newProviderSetUploadId, isIndexCreationPending, statusLoaded]);

  useEffect(() => {
    if (!!providerSet && !!selectedVersion && !selectedVersion.dateActivated) {
      providerSetService.getProviderSetVersionUploadModels(providerSet.providerSetId, selectedVersion.providerSetVersionId).then((uploadModel) => {
        // get the model with the most recent dateStarted and this version id
        let latestModel: ProviderSetUploadModel;
        uploadModel.forEach((model) => {
          if (!latestModel || new Date(model.dateStarted) > new Date(latestModel.dateStarted)) {
            latestModel = model;
          }
        })
        if (!!latestModel) {
          setNewProviderSetUploadId(latestModel.providerSetUploadId);
        }
      })
    }
  }, [selectedVersion]);

  const enableFilterCriteria = useMemo(() => {
    if (!providerSet || !providerSet.sourceProviderSetId || !selectedVersion)
      return false;

    return (!maxActivatedVersion ||
      (!selectedVersion.dateActivated && selectedVersion.dateCreated > maxActivatedVersion.dateActivated) ||
      selectedVersion.providerSetVersionId === maxActivatedVersion.providerSetVersionId) &&
      !providerSet.resource.isShared &&
      isOwner &&
      !selectedVersion.dateArchived;
  }, [providerSet, selectedVersion, isOwner, maxActivatedVersion]);

  const getVersionSelectOption = (version: ProviderSetVersionModel) => {
    const isInactive = !version.dateActivated &&
      !!maxActivatedVersion && maxActivatedVersion.dateActivated < version.dateCreated;

    return (
      <option value={version.providerSetVersionId.toString()} key={version.providerSetVersionId.toString()}>
        {moment.utc(version.dateCreated).local().format("MMM DD, YYYY h:mm:ss A") + (isInactive ? "*" : "")}
      </option>);
  }

  const versionOptions = useMemo(() => {
    let options: JSX.Element[] = [];

    if (!!providerSet && !!providerSet.providerSetVersions)
      options = providerSet.providerSetVersions
        .sort((a, b) => b.providerSetVersionId - a.providerSetVersionId)
        .map(x => getVersionSelectOption(x));

    return options;
  }, [providerSet, maxActivatedVersion]);

  const compareVersionOptions = useMemo(() => {
    let options: JSX.Element[] = [];

    if (!!providerSet && !!providerSet.providerSetVersions && !!selectedVersionId)
      options = providerSet.providerSetVersions
        .filter(x => x.providerSetVersionId.toString() !== selectedVersionId)
        .sort((a, b) => b.providerSetVersionId - a.providerSetVersionId)
        .map(x => getVersionSelectOption(x));

    return options;
  }, [providerSet, selectedVersionId, maxActivatedVersion]);

  const summaryInfoBody = useMemo(() => {
    let body = null;

    if (!!sourceProviderSet && selectedVersionOrigFilterCriteria.length === 0) {
      body = (<h2>Add criteria and save changes to view summary information!</h2>);
    } else if (!!sourceProviderSet && filterCriteriaHasChanges) {
      body = (<h2>Save changes to view summary information!</h2>)
    } else if (!!providerSet) {
      body = (
        <SummaryInfo
          providerSetId={providerSet.resource.resourceId.toString()}
          version={selectedVersion}
          compareVersion={selectedCompareVersion}
          activeAccordion={activeSummaryInfoAccordion}
          onUpdate={(response) => { setActiveSummaryInfoAccordion(response) }}
        />);
    }

    return body;
  }, [providerSet, selectedVersion, selectedCompareVersion, filterCriteriaHasChanges, selectedVersionOrigFilterCriteria, activeSummaryInfoAccordion])


  useEffect(() => {
    let interval: NodeJS.Timeout;

    const checkNPIDetailsSummaryJobs = async () => {
      if (providerSet && selectedVersion) {
        await providerSetService.createProviderSetNPIDetailsSummary(props.match.params.providerSetId, selectedVersion.providerSetVersionId.toString());
        const jobRunning = await providerSetService.getProviderSetNPIDetailsSummaryJobs(props.match.params.providerSetId, selectedVersion.providerSetVersionId.toString());
        setIsJobRunning(jobRunning);
        if (jobRunning) {
          setShowNPIModal(true);
          interval = setInterval(async () => {
            const jobStillRunning = await providerSetService.getProviderSetNPIDetailsSummaryJobs(props.match.params.providerSetId, selectedVersion.providerSetVersionId.toString());
            setIsJobRunning(jobStillRunning);
            if (!jobStillRunning) {
              clearInterval(interval);
              setShowNPIModal(false);
            }
          }, 5000);
        } else {
          setShowNPIModal(false);
        }
      }
    };

    if (selectedVersion) {
      checkNPIDetailsSummaryJobs();
    }

    return () => clearInterval(interval);
  }, [providerSet, selectedVersion, setIsJobRunning, showCreateIndexModal]);

  const validationInfoBody = useMemo(() => {
    let body = null;

    if (!isJobRunning && !!sourceProviderSet && selectedVersionOrigFilterCriteria.length === 0) {
      body = (<h2>Add criteria and save changes to view validation information!</h2>);
    } else if (!isJobRunning && !!sourceProviderSet && filterCriteriaHasChanges) {
      body = (<h2>Save changes to view validation information!</h2>)
    } else if (!isJobRunning && !!providerSet) {
      body = (
        <ValidationInfo
          providerSetId={providerSet.resource.resourceId.toString()}
          version={selectedVersion}
          compareVersion={selectedCompareVersion}
          activeAccordion={activeValidationInfoAccordion}
          onUpdate={(response) => { setActiveValidationInfoAccordion(response) }}
        />);
    }
    else if (isJobRunning) {
      setShowNPIModal(true);
      body = (<h2>An NPI Validation Job is currently running for this provider set.</h2>);
    }

    return body;
  }, [providerSet, selectedVersion, selectedCompareVersion, filterCriteriaHasChanges, selectedVersionOrigFilterCriteria, activeValidationInfoAccordion, isJobRunning]);

  const previewBody = useMemo(() => {
    let body = null;

    if (!!sourceProviderSet && selectedVersionOrigFilterCriteria.length === 0) {
      body = (<h2>Add criteria and save changes to view preview information!</h2>);
    } else if (!!sourceProviderSet && filterCriteriaHasChanges) {
      body = (<h2>Save changes to view preview information!</h2>)
    } else if (!!providerSet) {
      body = (
        <ProviderDetailsGrid
          providerSetId={providerSet.resource.resourceId.toString()}
          versionId={props.match.params.versionId}
          summaryType={ProviderSetVersionSummaryType.NoFilter}
          pageSettings={previewPageSettings}
          take={100}
          totalRecords={totalRecords}
          onUpdate={(response: ProviderDetailsGridOnUpdateResponse) => {
            setPreviewPageSettings(response.pageSettings);
          }
          }
        />)
    }
    return body;
  }, [providerSet, selectedVersionId, totalRecords, filterCriteriaHasChanges, selectedVersionOrigFilterCriteria, previewPageSettings]);

  const getHistoryEntry = (date: Date, details: JSX.Element, key: number): HistoryEntry => {
    return {
      date: moment(date),
      display: (
        <div className="mb-3" key={key.toString()}>
          <div className="font-weight-bold text-info">
            {moment.utc(date).local().format("L LT")}
          </div>
          <div>
            {details}
          </div>
        </div>)
    };
  }

  const historyBody = useMemo(() => {
    if (!providerSetHistory || !providerSet || !showHistory)
      return null;

    const hist: HistoryEntry[] = [];

    //ProviderSet Archived
    if (!!providerSetHistory.dateArchived) {
      hist.push(getHistoryEntry(providerSetHistory.dateArchived, (<>Network archived</>), hist.length));
    }

    //ProviderSet Created
    hist.push(getHistoryEntry(providerSetHistory.dateCreated, (<>Network created</>), hist.length));

    for (let i = 0; i < providerSetHistory.providerSetVersions.length; i++) {
      //ProviderSet Version Activated
      if (!!providerSetHistory.providerSetVersions[i].dateActivated)
        hist.push(getHistoryEntry(providerSetHistory.providerSetVersions[i].dateActivated, (<>Version activated</>), hist.length));

      //ProviderSet Version Created
      hist.push(getHistoryEntry(providerSetHistory.providerSetVersions[i].dateCreated, (<>New version created</>), hist.length));

      for (let j = 0; j < providerSetHistory.providerSetVersions[i].providerSetUpload.length; j++) {
        //Upload Finished
        if (!!providerSetHistory.providerSetVersions[i].providerSetUpload[j].dateFinished) {
          let uploadedFrom = "Quest Web API";

          if (providerSet.providerSetType === ProviderSetType.NJDOBI) {
            uploadedFrom = "NJDOBI Web API";
          } else if (providerSet.providerSetType === ProviderSetType.NYSDOH) {
            uploadedFrom = "PNDS Web API";
          }
          hist.push(
            getHistoryEntry(providerSetHistory.providerSetVersions[i].providerSetUpload[j].dateFinished,
              (<>
                Data uploaded from {uploadedFrom}
                <br />
                Group: {providerSetHistory.providerSetVersions[i].providerSetUpload[j].groupName}
                <br />
                Source: {providerSetHistory.providerSetVersions[i].providerSetUpload[j].sourceName}
              </>),
              hist.length));
        }
      }
    }

    return hist
      .sort((a, b) => b.date.valueOf() - a.date.valueOf())
      .map(x => x.display);
  }, [providerSetHistory, providerSet, showHistory]);

  const fetchProviderSetDetail = async (providerSetId: string) => {
    setPageLoading(true);
    setProviderSet(null);

    const provSet = await providerSetService.getProviderSet(providerSetId, true, true, false);

    if (provSet.providerSetType === ProviderSetType.NJDOBI || provSet.providerSetType === ProviderSetType.NYSDOH) {
      window.location.href = `${window.uiEndpoint}/Data/ProviderSets/${providerSetId}`;
      return;
    }

    let srcProvSet = null;
    let colNames: string[] = [];
    if (!!provSet.sourceProviderSetId) {
      srcProvSet = await providerSetService.getProviderSet(provSet.sourceProviderSetId.toString(), true, false, false);
      colNames = await providerSetService.getFilterProviderSetColumnNames(provSet.sourceProviderSetId.toString());
    }

    const hist = await providerSetService.getProviderSetHistory(providerSetId);

    setProviderSet(provSet);
    setShowNPIModal(isJobRunning);
    setSourceProviderSet(srcProvSet);
    const availFilterProvSet = provSet.filteredProviderSets.filter(x => !x.resource.dateArchived);
    setAvailableFilteredProviderSets(availFilterProvSet);
    setSelectedFilteredProviderActivateOptions(availFilterProvSet);
    setFilterColumnNames(colNames);
    setProviderSetHistory(hist);
    setPageLoading(false);
  }

  const handleArchive = () => {
    setSidePanelLoading(true);
    providerSetService.archiveProviderSet(providerSet.resource.resourceId.toString())
      .then(() => {
        fetchProviderSetDetail(providerSet.resource.resourceId.toString())
        setSidePanelLoading(false);
      }, () => {
        toast("An error occurred while archiving this network.",
          {
            containerId: "z-index-9999",
            type: "error"
          }
        );
        setSidePanelLoading(false);
      }
      );
  }

  const handleImportStatus = () => {
    console.log("handle import status");
  }

  const handlePurge = () => {
    setPageLoading(true);
    providerSetService.purgeProviderSet(providerSet.resource.resourceId.toString())
      .then(() => {
        history.replace(`/provider-sets/manager?req=${ProviderSetsRequestType.ArchivedNetworkProviders}`);
      }, () => {
        toast("An error occurred while purging this network.",
          {
            containerId: "z-index-9999",
            type: "error"
          }
        );
        setPageLoading(false);
      }
      );
  }

  const handleUnarchive = async () => {
    setPageLoading(true);
    providerSetService.unarchiveProviderSet(providerSet.resource.resourceId.toString())
      .then(() => {
        history.replace(`/provider-sets/manager`);
      }, () => {
        toast("An error occurred while unarchiving this network.",
          {
            containerId: "z-index-9999",
            type: "error"
          }
        );
        setPageLoading(false);
      }
      );
  }

  const handleFilterCriteriaChange = (response: FilterCriteriaOnChangeResponse) => {
    setFilterCriteriaHasChanges(response.hasChanges);
    setSelectedVersionFilterCriteria(response.criteria);
    setFilterInvalidRecords(response.filterInvalidRecords);
  }

  const toastError = (message: string) => {
    toast(
      <span>{message}</span>,
      {
        containerId: "z-index-9999",
        type: "error",
      },
    );
  }

  const handleAddNewVersion = (providerSetId: number, criteria: FilterCriteriaModel[], confirmPrompt: JSX.Element, afterSave: (newVersion: ProviderSetVersionModel) => void, afterError: () => void, filterInvalidRecords: boolean) => {
    setNewVersionModalContent({
      afterError,
      afterSave,
      confirmPrompt,
      criteria,
      filterInvalidRecords,
      providerSetId,
    });
    setShowConfirmModal(true);
  }

  const handleFilterCriteriaSave = (filterCriteria: FilterCriteriaModel[]) => {
    if (selectedVersion.providerSetVersionStatus === ProviderSetVersionStatusEnum.Activated) {
      handleAddNewVersion(
        providerSet.resource.resourceId,
        filterCriteria,
        <strong>Are you sure you want to save this filter?<br />Doing so will create a new version of the filtered network!</strong>,
        () => { fetchProviderSetDetail(providerSet.resource.resourceId.toString()); },
        () => { toastError("Filtered network criteria could not be updated!"); },
        filterInvalidRecords
      );
    } else {
      setPageLoading(true);
      providerSetService.providerSetUpdateFilter(selectedVersion.providerSetId, selectedVersion.providerSetVersionId, filterCriteria, filterInvalidRecords)
        .then(() => fetchProviderSetDetail(providerSet.resource.resourceId.toString()))
        .catch(() => {
          toastError("Filtered network criteria could not be updated!");
          setPageLoading(false);
        });
    }
  }

  const updateProviderSet = async (providerSetVersionId: number, name: string, description: string, isPrivate: boolean, sourceProviderSetId?: number, filterCriteria?: FilterCriteriaModel[]) => {
    const updateModel: UpdateProviderSetModel = {
      criteria: filterCriteria,
      description,
      isPrivate,
      name,
      sourceProviderSetId
    }

    setPageLoading(true);
    setSidePanelLoading(true);
    providerSetService.updateProviderSet(providerSet.resource.resourceId, providerSetVersionId, updateModel)
      .then(() => {
        setSidePanelLoading(false);
        fetchProviderSetDetail(providerSet.resource.resourceId.toString())
      })
      .catch(() => {
        toastError("Provider network could not be updated!");
        setSidePanelLoading(false);
        setPageLoading(false);
      });
  }

  const handleProviderSetDetailUpdate = async (name: string, description: string, isPrivate: boolean, sourceProviderSetId: number, filterCriteria: FilterCriteriaModel[]) => {
    if (!!sourceProviderSetId && !!sourceProviderSet && sourceProviderSetId !== sourceProviderSet.resource.resourceId) {
      handleAddNewVersion(
        providerSet.resource.resourceId,
        filterCriteria,
        <strong>Are you sure you want to save this filter?<br />Doing so will create a new version of the filtered network!</strong>,
        (newVersion: ProviderSetVersionModel) => { updateProviderSet(newVersion.providerSetVersionId, name, description, isPrivate, sourceProviderSetId, filterCriteria); },
        () => { toastError("Filtered network could not be updated!"); },
        filterInvalidRecords
      );
    } else {
      await updateProviderSet(selectedVersion.providerSetVersionId, name, description, isPrivate, null, null);
    }
  }

  const handleActivate = async () => {
    setPageLoading(true);
    localStorage.setItem('activateButtonDisabled', 'true');
    await providerSetService.activateProviderSet(
      providerSet.resource.resourceId.toString(),
      selectedVersionId,
      selectedFilteredProviderActivateOptions.map(x => x.resource.resourceId.toString())
    );
    fetchProviderSetDetail(providerSet.resource.resourceId.toString());
    localStorage.removeItem('activateButtonDisabled');
  }

  const queueProviderValidationJob = async () => {
    await providerValidationService.queueProviderValidationJob(selectedVersion.providerSetId, selectedVersion.providerSetVersionId);
  }

  useEffect(() => {
    const isButtonDisabled = localStorage.getItem('activateButtonDisabled') === 'true';
    if (isButtonDisabled) {
      if (saveButtonRef.current) {
        saveButtonRef.current.disabled = true;
      }
    }
  }, []);

  const handleFilteredProviderSetDetailSave = async (name: string, description: string, isPrivate: boolean, criteria: FilterCriteriaModel[], sourceProviderId: string, filterInvalidRecords: boolean) => {
    const createFilteredModel: CreateFilteredResourceModel = {
      criteria: criteria,
      description: description,
      filterInvalidRecords: filterInvalidRecords,
      isPrivate: isPrivate,
      name: name,
      sourceResourceId: sourceProviderId
    }

    setSidePanelLoading(true);
    providerSetService.addFilteredProviderSet(createFilteredModel)
      .then((response) => {
        setSidePanelLoading(false);
        history.push(`/provider-sets/${response.resource.resourceId}/detail`);
      }, () => {
        toast("An error occurred while creating this network.",
          {
            containerId: "z-index-9999",
            type: "error"
          }
        );
        setSidePanelLoading(false);
      }
      );
  }

  const handleCloneFilteredProviderSetDetailSave = async (name: string, description: string, isPrivate: boolean, filterCriteria: FilterCriteriaModel[], filterInvalidRecords: boolean) => {
    const createFilteredModel: CreateFilteredResourceModel = {
      criteria: filterCriteria,
      description: description,
      filterInvalidRecords: filterInvalidRecords,
      isPrivate: isPrivate,
      name: name,
      sourceResourceId: providerSet.sourceProviderSetId.toString()
    }

    setSidePanelLoading(true);
    providerSetService.addFilteredProviderSet(createFilteredModel)
      .then((response) => {
        setSidePanelLoading(false);
        history.push(`/provider-sets/${response.resource.resourceId}/detail`);
      }, () => {
        toast("An error occurred while cloning this network.",
          {
            containerId: "z-index-9999",
            type: "error"
          }
        );
        setSidePanelLoading(false);
      }
      );
  }

  return (
    <>
      {(
        <Page documentTitle={`${providerSet?.resource.name || "Loading"} - Provider Networks`}>
          <Breadcrumbs items={[...breadcrumbItemsBase, breadcrumbCurrentPage]} />
          {pageloading ? (<div className="ml-3 mt-3"><LoadingSpinner /></div>) :
            (<PageInner>
              <PageHeader title={(!!providerSet ? providerSet.resource.name : "")} iconName={QesIcons.FontAwesome.UserMd}>
                <PageHeaderLeft>
                  <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN, Roles.RESOURCE_ADMIN]}>
                    <span className="btn-group ml-1">
                      {showSharedBtn ? (
                        <span>
                          <i className="fa fa-share mr-1" />
                          <Link className="mr-2"
                            to={{
                              pathname: `/resources/share`,
                              state: {
                                breadcrumbItems: [...breadcrumbItemsBase, breadcrumbReturnLink],
                                isSingleResource: true,
                                resourceDescription: "Provider Network",
                                resourceId: props.match.params.providerSetId,
                                resourceTypes: [ResourceType.ProviderSet],
                              },
                            }}
                          >
                            Share
                          </Link>
                        </span>) :
                        (<></>)
                      }
                      {showEditBtn ? (
                        <span>
                          <i className="fa fa-pencil mr-1" />
                          <FlyoutLink
                            classNames="mr-2"
                            content={{
                              body: <EditPanelContent
                                providerSet={providerSet}
                                showIsPrivate={user.roles.some(x => x === Roles.RESOURCE_ADMIN)}
                                loading={sidePanelLoading}
                                handleSave={handleProviderSetDetailUpdate}
                                sourceProviderSet={sourceProviderSet}
                                sourceProviderSetVersion={selectedVersionSource}
                                filterCriteria={selectedVersionOrigFilterCriteria}
                                filterCriteriaColumnNames={filterColumnNames}
                                enableSourceEditing={enableFilterCriteria} />,
                              title: "Edit Network Details",
                            }}
                            size="40"
                            testId="provider-set-detail__edit-flyout"
                          >
                            Edit
                          </FlyoutLink>
                        </span>) :
                        (<></>)
                      }
                      {showCreateFilterBtn ? (
                        <span>
                          <i className="fa fa-filter mr-1" />
                          <FlyoutLink
                            classNames="mr-2"
                            content={{
                              body: <CreatePanelContent
                                createFiltered={true}
                                defaultSourceId={providerSet.resource.resourceId.toString()}
                                hasProviderValidations={!!selectedVersion && user.roles.includes(Roles.PROVIDER_VALIDATION_USER) && featureFlags.has(Features.CCIIO_PROVIDER_VALIDATIONS) ? selectedVersion.providerSetVersionValidationStatus == ProviderSetVersionValidationStatusEnum.Invalid : false}
                                loading={sidePanelLoading}
                                handleSave={handleFilteredProviderSetDetailSave} />,
                              title: "Add a New Filtered Network"
                            }}
                            testId="provider-set-detail__add-filtered-network-flyout"
                          >
                            Create Filtered Network
                          </FlyoutLink>
                        </span>) :
                        (<></>)
                      }
                      {showCloneFilterBtn ? (
                        <span>
                          <i className="fa fa-clone mr-1" />
                          <FlyoutLink
                            classNames="mr-2"
                            content={{
                              body: <ClonePanelContent
                                providerSet={providerSet}
                                sourceProviderSet={sourceProviderSet}
                                filterCriteria={selectedVersionOrigFilterCriteria}
                                filterCriteriaColumnNames={filterColumnNames}
                                loading={sidePanelLoading}
                                handleSave={handleCloneFilteredProviderSetDetailSave} />,
                              title: "Clone a Filtered Network"
                            }}
                            testId="provider-set-detail__clone-flyout"
                          >
                            Clone Filtered Network
                          </FlyoutLink>
                        </span>) :
                        (<></>)
                      }
                    </span>
                  </RoleRestricted>
                </PageHeaderLeft>
                <PageHeaderRight>
                  {!!providerSet && !providerSet.resource.isShared ? (
                    <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN, Roles.QUEST_USER]}>
                      {!!providerSet.resource.dateArchived && moment.utc(providerSet.resource.dateArchived).local() <= moment() ? (
                        <>
                          <QESButton qesType="primary" className="btn-light-gray mr-2" qesTestId="provider-detail__unarchive-button" onClick={handleUnarchive}>Unarchive</QESButton>
                          <FlyoutLink
                            classNames="qes-button-primary danger"
                            content={{
                              body: <PurgePanelContent
                                providerSet={providerSet}
                                loading={sidePanelLoading}
                                handlePurge={handlePurge}
                              />,
                              title: "Purge Provider Network",
                            }}
                            size="40"
                            testId="provider-set-details__purge-flyout"
                          >
                            Purge
                          </FlyoutLink>
                        </>) : (
                        <>
                          <RoleRestricted allowedRoles={[Roles.PROVIDER_NETWORK_IMPORT]}>
                            <QESButton qesType="secondary" className="mr-2" qesTestId="provider-detail__import-Status-button" onClick={handleImportStatus}>Import Status</QESButton>
                          </RoleRestricted>
                          <FlyoutLink
                            classNames="qes-button-secondary danger"
                            content={{
                              body: <ArchivePanelContent
                                providerSet={providerSet}
                                loading={sidePanelLoading}
                                handleArchive={handleArchive}
                              />,
                              title: "Archive Provider Network",
                            }}
                            size="40"
                            testId="provider-set-details__archive-flyout"
                          >
                            Archive
                          </FlyoutLink>
                        </>)
                      }
                    </RoleRestricted>) :
                    (<></>)
                  }
                </PageHeaderRight>
              </PageHeader>
              <PageSection>
                {!!providerSet ? (
                  <RoleRestricted allowedRoles={[Roles.RESOURCE_ADMIN]}>
                    <div className="mb-1">
                      <p>{(providerSet.resource.isPrivate ?
                        "This Network is Private (Can be administered only by you)." :
                        "This Network is not Private (Can be administered by you and other users authorized to administer non-Private networks).")}</p>
                    </div>
                  </RoleRestricted>) :
                  (<></>)
                }
                <div className="mb-1">
                  <p className="text-info">{(!!providerSet ? providerSet.resource.description : "")}</p>
                </div>
                <div className="mb-1">
                  <dl className="d-table">
                    {!!sourceProviderSet ?
                      (
                        <div className="d-table-row">
                          <dt className="d-table-cell font-weight-normal">Source Network:</dt>
                          <dd className="d-table-cell pl-2">
                            {(!!sourceProviderSet && !!selectedVersionSource ? `${sourceProviderSet.resource.name} - ${moment.utc(selectedVersionSource.dateCreated).local().format("MMM DD, YYYY h:mm:ss A")}` : "")}
                          </dd>
                        </div>
                      ) : (
                        <div className="d-table-row">
                          <dt className="d-table-cell font-weight-normal">Network Type:</dt>
                          <dd className="d-table-cell pl-2">{(!!providerSet ? providerSet.providerSetType.charAt(0).toUpperCase() + providerSet.providerSetType.slice(1) : "")}</dd>
                        </div>
                      )
                    }
                    <div className="d-table-row">
                      <dt className="d-table-cell font-weight-normal">Date Created: </dt>
                      <dd className="d-table-cell pl-2">{(!!selectedVersion ? moment.utc(selectedVersion.dateCreated).local().format("MMM DD, YYYY h:mm:ss A") : "")}</dd>
                    </div>
                    <div className="d-table-row">
                      <dt className="d-table-cell font-weight-normal">Date Last Updated: </dt>
                      <dd className="d-table-cell pl-2">{(!!selectedVersion ? moment.utc(selectedVersion.dateLastUpdated).local().format("MMM DD, YYYY h:mm:ss A") : "")}</dd>
                    </div>
                    <div className="d-table-row">
                      <dt className="d-table-cell font-weight-normal">Date Activated: </dt>
                      <dd className="d-table-cell pl-2">{(!!selectedVersion && !!selectedVersion.dateActivated ? moment.utc(selectedVersion.dateActivated).local().format("MMM DD, YYYY h:mm:ss A") : "")}</dd>
                    </div>
                  </dl>
                </div>
                <div className="mt-4 mb-3 d-flex flex-row w-100">
                  <QESSelect
                    testId={"provider-detail__compare"}
                    label="Compare To:&nbsp;"
                    placeholder="None"
                    allowEmpty={true}
                    onChange={(e) => setSelectedCompareVersionId(e.target.value)}
                    value={selectedCompareVersionId}
                    containerClassName="d-flex flex-row align-items-center"
                  >
                    {compareVersionOptions}
                  </QESSelect>
                  <QESSelect
                    testId={"provider-detail__version"}
                    label="Version:&nbsp;"
                    allowEmpty={false}
                    onChange={(e) => setSelectedVersionId(e.target.value)}
                    value={selectedVersionId}
                    containerClassName="d-flex flex-row align-items-center ml-auto"
                    qesRef={versionDropdownRef}
                  >
                    {versionOptions}
                  </QESSelect>
                </div>
              </PageSection>
              <PageSection>
                {alerts}
                {!filterCriteriaHasChanges && !!providerSet ?
                  (<OverallSummary providerSetId={providerSet.resource.resourceId.toString()} version={selectedVersion} compareVersion={selectedCompareVersion} onUpdate={(totRecs: number) => setTotalRecords(totRecs)} />) : null
                }
                {!!sourceProviderSet ?
                  (<FilterCriteria
                    fieldNames={filterColumnNames}
                    filterInvalidRecords={filterInvalidRecords}
                    defaultCriteria={selectedVersionOrigFilterCriteria}
                    onChange={(response) => handleFilterCriteriaChange(response)}
                    disabled={!enableFilterCriteria}
                    onSave={(response) => handleFilterCriteriaSave(response)}
                    saveButtonRef={saveButtonRef}
                    showFilterInvalidRecordsOption={!!sourceProviderSet && !!selectedVersionSource && selectedVersionSource.providerSetVersionValidationStatus == ProviderSetVersionValidationStatusEnum.Invalid && user.roles.includes(Roles.PROVIDER_VALIDATION_USER)}
                  />) : null
                }
                <div className="tabs-style-2">
                  <ul className="nav nav-tabs">
                    {!!providerSet && providerSet.providerSetType !== ProviderSetType.NJDOBI && providerSet.providerSetType !== ProviderSetType.NYSDOH ?
                      (<>
                        <li className="nav-item">
                          <a className={activeTab === "Summary Information" ? "nav-link active" : "nav-link"} role="tab" onClick={() => setActiveTab("Summary Information" as TabViews)}>
                            <i className="fa fa-files-o pr5" />Summary Information
                          </a>
                        </li>
                        <li className="nav-item">
                          <a className={activeTab === "Validation Information" ? "nav-link active" : "nav-link"} role="tab" onClick={() => setActiveTab("Validation Information" as TabViews)}>
                            <i className="fa fa-check pr5" />Validation Information
                          </a>
                        </li>
                      </>) : null
                    }
                    {!selectedCompareVersion ? (
                      <li className="nav-item">
                        <a className={activeTab === "Preview" ? "nav-link active" : "nav-link"} role="tab"
                          onClick={() => {
                            setActiveTab("Preview" as TabViews);
                          }}>
                          <i className="fa fa-eye pr5" />Preview
                        </a>
                      </li>) : null
                    }
                  </ul>
                </div>
                <div className="mt-3">
                  {activeTab === "Summary Information" ?
                    (<>{summaryInfoBody}</>) : null
                  }
                  {activeTab === "Validation Information" ?
                    (<>{validationInfoBody}</>) : null
                  }
                  {activeTab === "Preview" ?
                    (<>{previewBody}</>) : null
                  }
                </div>
              </PageSection>
              <PageSection className="mt-4">
                {showHistory ? (
                  <AccordionContainer>
                    <Accordion title={<><QesIcon isDecorative iconName={QesIcons.FontAwesome.History} width="12" classNames="align-baseline mr-1" />History Log</>} testId="provider-set-detail__history-log-accordion">
                      {historyBody}
                    </Accordion>
                  </AccordionContainer>) : null
                }
              </PageSection>
            </PageInner>)
          }
          <Modal
            isVisible={showConfirmModal && !!newVersionModalContent}
            closeModal={() => setShowConfirmModal(false)}
            hideBodyScroll={true}
            content={{
              body: (
                <>
                  <p><i className="fa fa-exclamation-triangle text-warning" /><strong> Warning!</strong></p>
                  <p>
                    <strong>{newVersionModalContent?.confirmPrompt}</strong>
                  </p>
                </>
              ),
              cancelButtonText: "Cancel",
              confirmButtonAction: () => {
                setPageLoading(true);
                providerSetService.addProviderSetVersion(newVersionModalContent?.providerSetId, newVersionModalContent?.criteria, newVersionModalContent?.filterInvalidRecords)
                  .then((newVersion) => {
                    history.replace(`/provider-sets/${props.match.params.providerSetId}/detail`);
                    newVersionModalContent?.afterSave(newVersion);
                  })
                  .catch(() => {
                    newVersionModalContent?.afterError();
                    setPageLoading(false);
                  });
                setShowConfirmModal(false);
              },
              confirmButtonText: "Confirm",
              title: "Create New Version",
            }}
            triggerElement={saveButtonRef}
          />
          <Modal
            isVisible={showCreateIndexModal}
            closeModal={() => {
              if (!selectedVersion) {
                history.goBack();
              } else {
                setSelectedVersionId(props.match.params.versionId);
                setIndexModalWarningOverride(false);
                setShowCreateIndexModal(false);
              }
            }}
            hideBodyScroll={true}
            content={{
              body: (
                <>
                  (
                  <>
                    Indexes do not exist for this provider network version, and they must be created before you can continue. Click &quot;Confirm&quot; to create the indexes.
                    {!isVersionUploadFinished ?
                      <>
                        <br /><br />
                        <strong>Warning</strong>
                        <br />
                        The upload is not complete. Creating indexes now will slow down the ongoing upload process.
                        <QESCheckbox
                          testId={"provider-set-index-prompt-warning-override"}
                          label="I understand the above warning and wish to continue."
                          checked={indexModalWarningOverride}
                          onChange={(e) => setIndexModalWarningOverride(e.target.checked)}
                        />
                      </>
                      : null}
                  </>
                  )
                </>
              ),
              cancelButtonText: "Cancel",
              confirmButtonAction: () => {
                setPageLoading(true);
                providerSetService.createProviderSetVersionIndexesAsync(props.match.params.providerSetId, selectedVersionId).then(() => {
                  updateVersionInfo(selectedVersionId);
                  setShowCreateIndexModal(false);
                  setIndexModalWarningOverride(false);
                  setIsIndexCreationPending(true);
                  setPageLoading(false);
                });
              },
              confirmButtonDisable: !isVersionUploadFinished && !indexModalWarningOverride,
              confirmButtonText: "Confirm",
              title: "Create Provider Network Version Indexes",
            }}
            triggerElement={versionDropdownRef}
          />
          <Modal
            isVisible={showNPIModal}
            closeModal={() => setShowNPIModal(false)}
            hideBodyScroll={true}
            content={{
              body: (
                <>
                  An NPI Inspection Details Summary Job is currently being processed. NPI Inspection Details will not be available while the job is running. This process may take a while to complete on larger provider sets.
                </>
              ),
              cancelButtonText: "",
              confirmButtonAction: () => {
                setShowNPIModal(false);
              },
              confirmButtonText: "Ok",
              title: "NPI Details Summary Job",
            }}
          />
        </Page>
      )
      }
    </>
  );
}